import React, { useMemo } from 'react';

// Golden constellation / celestial line-art decorations (subtle background layer)
const Gold = ({ style, children, size = 60, opacity = 0.5 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size} fill="none"
    stroke="#c9a24b" strokeWidth="1.4" style={{ position: 'absolute', opacity, ...style }}>
    {children}
  </svg>
);

const SunBurst = (p) => (
  <Gold {...p}>
    <circle cx="50" cy="50" r="14" fill="#c9a24b" fillOpacity="0.25" />
    {Array.from({ length: 16 }).map((_, i) => {
      const a = (i * Math.PI * 2) / 16; const r1 = 18; const r2 = 34;
      return <line key={i} x1={50 + Math.cos(a) * r1} y1={50 + Math.sin(a) * r1}
        x2={50 + Math.cos(a) * r2} y2={50 + Math.sin(a) * r2} />;
    })}
    <ellipse cx="50" cy="50" rx="44" ry="16" strokeDasharray="2 5" transform="rotate(20 50 50)" />
  </Gold>
);

const Crescent = (p) => (
  <Gold {...p}>
    <path d="M60 20a32 32 0 1 0 0 60 26 26 0 1 1 0-60z" fill="#c9a24b" fillOpacity="0.18" />
    {[[20,15],[80,30],[30,80],[70,78]].map(([x,y],i)=>(<circle key={i} cx={x} cy={y} r="1.6" fill="#c9a24b"/>))}
  </Gold>
);

const Planet = (p) => (
  <Gold {...p}>
    <circle cx="50" cy="50" r="20" />
    <ellipse cx="50" cy="50" rx="38" ry="12" transform="rotate(-18 50 50)" />
  </Gold>
);

const Constel = ({ pts, style, size = 120, opacity = 0.45 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size} style={{ position: 'absolute', opacity, ...style }}>
    <polyline points={pts.map((p) => p.join(',')).join(' ')} fill="none" stroke="#c9a24b" strokeWidth="1" />
    {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="1.8" fill="#d8b45c" />)}
  </svg>
);

export default function StarField({ gold = true, className = '' }) {
  const stars = useMemo(() => Array.from({ length: 90 }).map(() => ({
    top: Math.random() * 100,
    left: Math.random() * 100,
    size: Math.random() * 2 + 0.6,
    delay: Math.random() * 4,
    bright: Math.random() > 0.7,
  })), []);

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
      style={{ background: 'radial-gradient(120% 90% at 50% 0%, #0d0f26 0%, #07081a 45%, #030410 100%)' }}>
      {stars.map((s, i) => (
        <span key={i} className="twinkle" style={{
          position: 'absolute', top: `${s.top}%`, left: `${s.left}%`,
          width: s.size, height: s.size, borderRadius: '50%',
          background: s.bright ? '#dfe0ff' : '#8f8fc4',
          boxShadow: s.bright ? '0 0 6px 1px rgba(200,200,255,.8)' : 'none',
          animationDelay: `${s.delay}s`,
        }} />
      ))}
      {gold && (
        <>
          <SunBurst size={90} style={{ top: '6%', left: '18%' }} opacity={0.4} />
          <SunBurst size={70} style={{ top: '58%', left: '2%' }} opacity={0.35} />
          <Crescent size={64} style={{ top: '30%', right: '6%' }} opacity={0.4} />
          <Crescent size={52} style={{ bottom: '20%', left: '10%' }} opacity={0.35} />
          <Planet size={80} style={{ top: '44%', right: '10%' }} opacity={0.4} />
          <Planet size={64} style={{ top: '12%', right: '24%' }} opacity={0.3} />
          <Constel pts={[[6,18],[16,10],[24,22],[34,14],[30,30]]} style={{ top: '20%', left: '2%' }} />
          <Constel pts={[[70,70],[80,64],[88,74],[82,84]]} style={{ bottom: '8%', right: '4%' }} size={90} />
          <Constel pts={[[10,60],[20,66],[16,78],[28,80]]} style={{ bottom: '22%', left: '30%' }} size={80} opacity={0.35} />
        </>
      )}
    </div>
  );
}
